/**
 * Flow editor — tiny hit event bus.
 *
 * The 3D scene's per-frame hit-detection loop emits a pulse whenever a note head
 * or trail tail crosses the strike line during playback. The interactive
 * background visual subscribes to these to spawn particle bursts that stay in
 * sync with the map (the most musically-aligned signal we have without a live
 * analyser).
 *
 * Module-level (not a store) so emitting from the render loop costs nothing and
 * never triggers React re-renders.
 */

export interface FlowHit {
  /** Lane centre as a fraction across the track, 0 (far left) … 1 (far right). */
  xFrac: number;
  /** Gameplay lane colour (hex) of the note. */
  color: string;
  /** True for stronger accents (trail tails) so visuals can punch harder. */
  strong: boolean;
}

type HitListener = (hit: FlowHit) => void;

const listeners = new Set<HitListener>();

export function onFlowHit(fn: HitListener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function emitFlowHit(hit: FlowHit): void {
  for (const fn of listeners) {
    try { fn(hit); } catch { /* a bad listener must not break the loop */ }
  }
}
